import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { useSelector, useDispatch } from 'react-redux';
import api from '../../services/api';
import Button from '../common/Button';
import VaultSidebar from '../vault/VaultSidebar';
import VaultHeader from '../vault/VaultHeader';
import { updateMfaStatus } from '../../store/slices/authSlice';
import { ShieldCheckIcon, QrCodeIcon, DevicePhoneMobileIcon } from '@heroicons/react/24/outline';

const MfaSetup: React.FC = () => {
    const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
    const [secret, setSecret] = useState<string | null>(null);
    const [verificationCode, setVerificationCode] = useState<string>('');
    const dispatch = useDispatch();

    const isEnabled = useSelector((state: any) => state.auth.user?.two_factor_enabled);


    const setupMfa = async () => {
        try {

            const response = await api.post('/users/2fa/setup', { secret: secret });
            setQrCodeUrl(response.qrCodeUrl);
            setSecret(response.secret);
        } catch (error) {
            console.error('Error during MFA setup:', error);
            toast.error('Failed to setup MFA');
        }
    };

    const verifyMfa = async () => {
        try {
            const response = await api.post('/users/2fa/verify', { code: verificationCode });
            if (response.success) {
                dispatch(updateMfaStatus(true));
                toast.success('MFA enabled successfully');
            } else {
                toast.error('Invalid verification code');
            }
        } catch (error) {
            toast.error('Failed to verify MFA code');
        }
    };

    const disableMfa = async () => {
        try {
            if (!window.confirm('Are you sure you want to disable MFA?')) {
                return;
            }
            const response = await api.post('/users/2fa/disable', { code: verificationCode });
            if (response.success) {
                dispatch(updateMfaStatus(false));
                toast.success('MFA disabled successfully');
            } else {
                toast.error('Invalid verification code');
            }
        } catch (error) {
            toast.error('Failed to disable MFA');
        }
    };

    return (
        <div className="flex h-screen bg-gray-100">
            <VaultSidebar />
            <div className="flex-1 flex flex-col overflow-hidden">
                <VaultHeader />
                <main className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900">
                    {/* Hero Banner Section */}
                    <div className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 dark:from-primary-800 dark:via-primary-900 dark:to-gray-900 overflow-hidden">
                        {/* Background Pattern */}
                        <div className="absolute inset-0 opacity-10">
                            <div className="absolute top-0 right-0 w-96 h-48 bg-white rounded-full -translate-y-24 translate-x-48"></div>
                            <div className="absolute bottom-0 left-0 w-72 h-36 bg-white rounded-full translate-y-18 -translate-x-36"></div>
                            <div className="absolute top-1/2 left-1/4 w-32 h-32 bg-white rounded-full"></div>
                            <div className="absolute top-1/3 right-1/3 w-24 h-24 bg-white rounded-full"></div>
                        </div>

                        <div className="relative px-6 py-12">
                            <div className="max-w-7xl mx-auto">
                                {/* Header Content */}
                                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-8">
                                    <div className="mb-6 lg:mb-0">
                                        <div className="flex items-start space-x-4 mb-4">
                                            <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl border border-white/30">
                                                <ShieldCheckIcon className="h-8 w-8 text-white" />
                                            </div>
                                            <div>
                                                <h1 className="text-4xl lg:text-5xl font-bold text-white mb-2 leading-tight">
                                                    Multi-Factor Authentication
                                                </h1>
                                                <div className="flex items-center space-x-2 text-white/80">
                                                    <DevicePhoneMobileIcon className="h-5 w-5" />
                                                    <span className="text-sm">Advanced Security Center</span>
                                                </div>
                                            </div>
                                        </div>
                                        <p className="text-xl text-white/90 max-w-2xl leading-relaxed">
                                            Enhance your account security with an additional layer of protection.
                                            Setup or manage your two-factor authentication settings.
                                        </p>
                                    </div>

                                    {/* Status Card */}
                                    <div className="bg-white/20 backdrop-blur-sm rounded-xl p-6 border border-white/30 min-w-[300px]">
                                        <div className="flex items-center space-x-4">
                                            <div className={`p-3 rounded-full ${isEnabled ? 'bg-green-500/20' : 'bg-orange-500/20'}`}>
                                                {isEnabled ? 
                                                    <ShieldCheckIcon className="h-8 w-8 text-white" /> :
                                                    <QrCodeIcon className="h-8 w-8 text-white" />
                                                }
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-semibold text-white">
                                                    {isEnabled ? 'MFA Active' : 'MFA Setup Required'}
                                                </h3>
                                                <p className="text-white/80 text-sm">
                                                    {isEnabled ? '🛡️ Your account is protected' : '⚠️ Setup recommended'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Security Stats */}
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                                    <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30 hover:bg-white/25 transition-all duration-200">
                                        <div className="flex items-center space-x-3">
                                            <div className="p-2 bg-white/20 rounded-lg">
                                                <ShieldCheckIcon className="w-6 h-6 text-white" />
                                            </div>
                                            <div>
                                                <div className="text-lg font-bold text-white">
                                                    {isEnabled ? 'Enabled' : 'Disabled'}
                                                </div>
                                                <div className="text-xs text-white/80 uppercase tracking-wide">
                                                    MFA Status
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30 hover:bg-white/25 transition-all duration-200">
                                        <div className="flex items-center space-x-3">
                                            <div className="p-2 bg-white/20 rounded-lg">
                                                <DevicePhoneMobileIcon className="w-6 h-6 text-white" />
                                            </div>
                                            <div>
                                                <div className="text-lg font-bold text-white">
                                                    TOTP
                                                </div>
                                                <div className="text-xs text-white/80 uppercase tracking-wide">
                                                    Auth Method
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4 border border-white/30 hover:bg-white/25 transition-all duration-200">
                                        <div className="flex items-center space-x-3">
                                            <div className="p-2 bg-white/20 rounded-lg">
                                                <QrCodeIcon className="w-6 h-6 text-white" />
                                            </div>
                                            <div>
                                                <div className="text-lg font-bold text-white">
                                                    6-Digit
                                                </div>
                                                <div className="text-xs text-white/80 uppercase tracking-wide">
                                                    Code Length
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Content Section */}
                    <div className="max-w-4xl mx-auto px-6 py-8">
                        <div className="space-y-6">

                            {/* Info Section */}
                            <div className="bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-2xl p-6 border-2 border-blue-200 dark:border-blue-700 mb-6">
                                <div className="flex items-start space-x-4">
                                    <div className="p-3 bg-blue-500/20 rounded-xl">
                                        <ShieldCheckIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-3">
                                            🛡️ Why use Multi-Factor Authentication?
                                        </h3>
                                        <p className="text-blue-800 dark:text-blue-200 leading-relaxed">
                                            MFA adds an extra layer of security to your account by requiring a second form of verification beyond your password.
                                            Even if your password is compromised, your account remains protected.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {!isEnabled ? (
                                <div className="space-y-6">
                                    {/* Setup Instructions Card */}
                                    <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl overflow-hidden border-2 border-gray-300 dark:border-gray-600">
                                        <div className="bg-gradient-to-r from-primary-600 to-primary-800 px-6 py-4">
                                            <h3 className="text-lg font-semibold text-white flex items-center">
                                                <QrCodeIcon className="h-6 w-6 mr-3" />
                                                Setup Instructions
                                            </h3>
                                            <p className="text-purple-100 text-sm mt-1">Follow these steps to secure your account</p>
                                        </div>

                                        <div className="px-6 py-6">
                                            <ol className="space-y-4">
                                                <li className="flex items-start space-x-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                                                    <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                                                        1
                                                    </div>
                                                    <div>
                                                        <h4 className="font-medium text-gray-900 dark:text-white mb-2">Download an authenticator app:</h4>
                                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                                            <div className="flex items-center space-x-2 p-3 bg-white dark:bg-gray-600 rounded-lg border">
                                                                <span className="text-2xl">📱</span>
                                                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Google Authenticator</span>
                                                            </div>
                                                            <div className="flex items-center space-x-2 p-3 bg-white dark:bg-gray-600 rounded-lg border">
                                                                <span className="text-2xl">🔐</span>
                                                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Microsoft Authenticator</span>
                                                            </div>
                                                            <div className="flex items-center space-x-2 p-3 bg-white dark:bg-gray-600 rounded-lg border">
                                                                <span className="text-2xl">🛡️</span>
                                                                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Authy</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </li>
                                                <li className="flex items-start space-x-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                                                    <div className="flex-shrink-0 w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                                                        2
                                                    </div>
                                                    <div>
                                                        <h4 className="font-medium text-gray-900 dark:text-white">Click "Setup MFA" below to generate your unique QR code</h4>
                                                    </div>
                                                </li>
                                                <li className="flex items-start space-x-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                                                    <div className="flex-shrink-0 w-8 h-8 bg-purple-500 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                                                        3
                                                    </div>
                                                    <div>
                                                        <h4 className="font-medium text-gray-900 dark:text-white">Scan the QR code with your authenticator app</h4>
                                                    </div>
                                                </li>
                                                <li className="flex items-start space-x-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                                                    <div className="flex-shrink-0 w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                                                        4
                                                    </div>
                                                    <div>
                                                        <h4 className="font-medium text-gray-900 dark:text-white">Enter the 6-digit code shown in your app to verify</h4>
                                                    </div>
                                                </li>
                                            </ol>
                                        </div>
                                    </div>

                                    {!qrCodeUrl ? (
                                        <div className="text-center">
                                            <Button 
                                                onClick={setupMfa}
                                                className="bg-gradient-to-r from-primary-500 to-primary-900 text-white px-8 py-4 rounded-xl font-semibold flex items-center space-x-3 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 mx-auto"
                                            >
                                                <QrCodeIcon className="h-6 w-6" />
                                                <span>🚀 Setup MFA</span>
                                            </Button>
                                        </div>
                                    ) : (
                                        <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl overflow-hidden border-2 border-gray-300 dark:border-gray-600">
                                            <div className="bg-gradient-to-r from-primary-600 to-primary-800 px-6 py-4">
                                                <h3 className="text-lg font-semibold text-white flex items-center">
                                                    <QrCodeIcon className="h-6 w-6 mr-3" />
                                                    Scan QR Code
                                                </h3>
                                                <p className="text-green-100 text-sm mt-1">Use your authenticator app to scan this code</p>
                                            </div>
                                            
                                            <div className="px-6 py-8 text-center space-y-6">
                                                <div className="inline-block p-6 bg-gray-50 dark:bg-gray-700 rounded-2xl">
                                                    <img 
                                                        src={qrCodeUrl} 
                                                        alt="QR Code" 
                                                        className="mx-auto border-4 border-white dark:border-gray-600 rounded-xl shadow-lg"
                                                    />
                                                </div>
                                                
                                                <div className="max-w-xs mx-auto">
                                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                                                        Enter 6-digit verification code:
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={verificationCode}
                                                        onChange={(e) => {
                                                            const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                                                            setVerificationCode(value);
                                                        }}
                                                        placeholder="000000"
                                                        className="w-full px-4 py-3 text-lg font-mono text-center border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all duration-200"
                                                        maxLength={6}
                                                    />
                                                </div>
                                                
                                                <Button 
                                                    onClick={verifyMfa}
                                                    className="bg-gradient-to-r from-primary-600 to-primary-800 text-white px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                                                >
                                                    ✅ Verify & Enable MFA
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    {/* Enabled Status Card */}
                                    <div className="bg-gradient-to-br from-primary-600 to-primary-800 dark:from-green-900/20 dark:to-emerald-900/20 rounded-2xl p-6 border-2 border-green-200 dark:border-green-700">
                                        <div className="flex items-center space-x-4">
                                            <div className="p-4 bg-white rounded-full">
                                                <DevicePhoneMobileIcon className="h-10 w-10 text-white-600 dark:text-green-400" />
                                            </div>
                                            <div>
                                                <h3 className="text-xl font-semibold text-white dark:text-green-100">
                                                    🎉 MFA is Active!
                                                </h3>
                                                <p className="text-white dark:text-green-200">
                                                    Your account is protected with multi-factor authentication.
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Disable MFA Card */}
                                    <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl overflow-hidden border-2 border-gray-300 dark:border-gray-600">
                                        <div className="bg-gradient-to-r from-primary-600 to-primary-800 px-6 py-4">
                                            <h3 className="text-lg font-semibold text-white">⚠️ Disable MFA</h3>
                                            <p className="text-red-100 text-sm mt-1">Remove multi-factor authentication (not recommended)</p>
                                        </div>
                                        
                                        <div className="px-6 py-6">
                                            <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                                                To disable MFA, enter a verification code from your authenticator app.
                                                <span className="block mt-2 text-sm text-red-600 dark:text-red-400">
                                                    ⚠️ Warning: This will make your account less secure.
                                                </span>
                                            </p>
                                            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                                                <input
                                                    type="text"
                                                    value={verificationCode}
                                                    onChange={(e) => {
                                                        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                                                        setVerificationCode(value);
                                                    }}
                                                    placeholder="Enter verification code"
                                                    className="flex-1 px-4 py-3 font-mono text-center border-2 border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white transition-all duration-200"
                                                    maxLength={6}
                                                />
                                                <Button 
                                                    onClick={disableMfa}
                                                    className="bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-700 hover:to-pink-700 text-white px-6 py-3 rounded-xl font-semibold border-2 border-transparent hover:scale-105 transition-all duration-200"
                                                >
                                                    🚫 Disable MFA
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default MfaSetup;